<template>
  <DSFieldset
    :id="props.id"
    ref="fieldsetRef"
    :class="DS_BUTTON_GROUP_CLASSES.Root"
    :direction="DS_DIRECTION.Horizontally"
    :with-divider="false"
    :disabled="props.disabled"
    :aria-label="props.ariaLabel"
    :aria-describedby="props.ariaDescribedBy"
    role="radiogroup"
    data-cy="ds-button-group"
    @focusin="onFocus"
    @focusout="onBlur"
  >
    <DSButton
      v-for="option in props.options"
      :key="option.value"
      ref="buttonRefs"
      :class="DS_BUTTON_GROUP_CLASSES.Option"
      :variant="isSelected(option) ? DS_BUTTON_VARIANT.Primary : DS_BUTTON_VARIANT.Secondary"
      :size="props.size"
      :disabled="isDisabled(option)"
      :prefix-icon="option.prefixIcon"
      :suffix-icon="option.suffixIcon"
      :aria-label="option.ariaLabel"
      :value="option.value"
      :tabindex="getTabindex(option)"
      :aria-checked="isSelected(option)"
      role="radio"
      :data-cy="`ds-button-group__option--${option.value}`"
      @click="selectOption(option)"
      @keydown="onKeydown($event, option)"
    >
      {{ option.label }}
    </DSButton>
    <input
      v-if="props.name && selectedOption"
      type="hidden"
      :name="props.name"
      :value="model"
      :disabled="isDisabled(selectedOption)"
      data-cy="ds-button-group__input"
    />
  </DSFieldset>
</template>

<script setup lang="ts">
import DSButton from "@/src/design_system/button_and_controls/button/components/DSButton.vue";
import { DS_BUTTON_VARIANT } from "@/src/design_system/button_and_controls/button/types/props";
import DSFieldset from "@/src/design_system/form_elements/fieldset/components/DSFieldset.vue";
import { crossesFocusBoundary } from "@/src/design_system/form_elements/shared/utils/focusBoundary";
import { DS_DIRECTION } from "@/src/design_system/shared/types/direction";
import { useDSButtonGroup } from "../composables/useDSButtonGroup";
import { DS_BUTTON_GROUP_CLASSES } from "../constants/classes";
import type { DSButtonGroupEmits } from "../types/emits";
import { DS_BUTTON_GROUP_SIZE, type DSButtonGroupProps } from "../types/props";

const props = withDefaults(defineProps<DSButtonGroupProps>(), {
  size: DS_BUTTON_GROUP_SIZE.Medium,
  disabled: false,
});
const model = defineModel<string>();
const emit = defineEmits<DSButtonGroupEmits>();
const {
  buttonRefs,
  fieldsetRef,
  rootElement,
  selectedOption,
  isDisabled,
  isSelected,
  getTabindex,
  selectOption,
  onKeydown,
  focus,
  blur,
} = useDSButtonGroup(props, model);

const onFocus = (event: FocusEvent) => {
  if (crossesFocusBoundary(event, rootElement())) emit("focus", event);
};
const onBlur = (event: FocusEvent) => {
  if (crossesFocusBoundary(event, rootElement())) emit("blur", event);
};

defineExpose({ focus, blur });
</script>
